import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
} from '@mui/material';
import {
  CreateTaskInput,
  TaskPriority,
  TaskTopic,
} from '../../../types/interfaces/task.types';
import * as taskService from '../../../services/taskService';
import { MDInput } from '../../../components/MDInput';
import { MDButton } from '../../../components/MDButton';
import { MDTypography } from '../../../components/MDTypography';
import { useAuthContext } from 'contexts/authContext';
import Select from 'components/Controllers/Select/Select';
import { logger } from 'services/logger';
import { AxiosError, isAxiosError } from 'axios';
import { useTasks } from 'hooks/useTasks';

interface CreateTaskDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (input: CreateTaskInput) => Promise<void>;
}

const formatEnumText = (text: string) => {
  return text
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

type CaseIdentifierFieldProps = {
  actor: 'FAIRFARE' | 'CARRIER' | 'NETWORK';
  value: string;
  onChange: (value: string) => void;
};

const CaseIdentifierField = ({
  actor,
  value,
  onChange,
}: CaseIdentifierFieldProps) => {
  const label = actor === 'CARRIER' ? 'Case ID' : 'Policy Number';

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
      <MDTypography
        variant="body2"
        sx={{
          fontWeight: 500,
          color: 'text.secondary',
          width: '30%',
          pr: 2,
        }}
      >
        {label}
      </MDTypography>
      <Box sx={{ flex: 1 }}>
        <MDInput
          fullWidth
          variant="outlined"
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </Box>
    </Box>
  );
};

const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({
  open,
  onClose,
  onCreate,
}) => {
  const [topics, setTopics] = useState<TaskTopic[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [selectedTopicCategory, setSelectedTopicCategory] =
    useState('');
  const [selectedTopicDescription, setSelectedTopicDescription] = useState('');
  const [additionalDescription, setAdditionalDescription] = useState('');
  const [caseIdentifier, setCaseIdentifier] = useState('');
  // const [title, setTitle] = useState('');
  // const [taskPriority, setTaskPriority] = useState<TaskPriority>(
  //   TaskPriority.NORMAL
  // );
  const [error, setError] = useState<string | null>(null);
  // const [tenants, setTenants] = useState<
  //   TaskTenantsListForNetworkResponse | undefined
  // >();
  // const [selectedTenant, setSelectedTenant] = useState('');
  const { tenants, selectedTenant, setSelectedTenant } = useTasks();
  const { user } = useAuthContext();

  useEffect(() => {
    const fetchTopics = async () => {
      setLoadingTopics(true);
      try {
        const topicsData = await taskService.getTopics();
        setTopics(topicsData);
      } catch (error) {
        logger.error('Failed to fetch topics:', {
          error: (error as AxiosError).message,
        });
        setError('Failed to load topics. Please try again later.');
      } finally {
        setLoadingTopics(false);
      }
    };
    if (open) {
      fetchTopics();
    }
  }, [open]);

  const handleSubmit = async () => {
    const selectedTopic = topics.find(
      (topic) =>
        topic.name === selectedTopicCategory &&
        topic.description === selectedTopicDescription
    );
    if (!selectedTopic) {
      setError('Please select a valid topic category and description.');
      return;
    }
    try {
      await onCreate({
        title: '',
        topicId: selectedTopic.id,
        topicCategory: selectedTopicCategory,
        topicDescription: selectedTopicDescription,
        additionalDescription,
        taskPriority: TaskPriority.NORMAL,
        caseIdentifier,
        caseIdentifierType: user?.userActor === 'CARRIER' ? 'caseId' : 'policyNumber',
        carrierTenantId: selectedTenant,
      });
    } catch (error) {
      if (isAxiosError(error)) {
        logger.error('Failed to create task', {
          error: error.message,
        });
      }
      setError('Failed to create task. Please try again later.');
      return;
    }
    handleClose();
  };

  const handleClose = useCallback(() => {
    setError(null);
    // setTitle('');
    setSelectedTopicCategory('');
    setSelectedTopicDescription('');
    setAdditionalDescription('');
    // setTaskPriority(TaskPriority.NORMAL);
    setCaseIdentifier('');
    onClose();
  }, [onClose]);

  const topicCategoryToTopicDescription = topics.reduce((acc, topic) => {
    if (topic.description) {
      if (!acc[topic.name]) {
        acc[topic.name] = [topic.description];
      } else {
        acc[topic.name].push(topic.description);
      }
    }
    return acc;
  }, {} as Record<string, string[]>);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Task</DialogTitle>
      <DialogContent style={{ paddingTop: 10 }}>
        {error && (
          <MDTypography color="error" variant="body2" sx={{ mb: 2 }}>
            {error}
          </MDTypography>
        )}
        <Grid container spacing={2}>
          {/* Topic - Full Row */}
          {user?.userActor !== 'CARRIER' && (
            <Grid item xs={12}>
              <Select
                label="Client"
                value={selectedTenant}
                onChange={(value) => setSelectedTenant(value)}
                options={
                  tenants?.tenants.map((tenant) => ({
                    key: tenant.id.toString(),
                    label: tenant.name,
                  })) || []
                }
                disabled={loadingTopics || !!error}
              ></Select>
            </Grid>
          )}

          {/* <Grid item xs={12}>
            <MDInput
              fullWidth
              label="Title"
              variant="outlined"
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={loadingTopics || !!error}
            />
          </Grid> */}
          
          <Grid item xs={12}>
            <Select
              label="Task Category"
              value={selectedTopicCategory}
              onChange={(value) => setSelectedTopicCategory(value)}
              options={Object.keys(topicCategoryToTopicDescription).map(
                (topicCategory) => ({
                  key: topicCategory,
                  label: topicCategory,
                })
              )}
              disabled={loadingTopics || !!error}
            ></Select>
          </Grid>
          <Grid item xs={12}>
            <Select
              label="Task Description"
              value={selectedTopicDescription}
              onChange={(value) => setSelectedTopicDescription(value)}
              options={
                (topicCategoryToTopicDescription[selectedTopicCategory] &&
                  topicCategoryToTopicDescription[selectedTopicCategory].map(
                    (description) => ({
                      key: description,
                      label: description,
                    })
                  )) ||
                []
              }
              disabled={
                loadingTopics ||
                !selectedTopicCategory ||
                selectedTopicCategory === 'Choose a topic' ||
                !!error
              }
            ></Select>
          </Grid>
          {/* Additional Details - Full Row */}
          <Grid item xs={12}>
            <MDInput
              fullWidth
              multiline
              rows={3}
              label="Additional Details (optional)"
              variant="outlined"
              size="small"
              value={additionalDescription}
              onChange={(e) => setAdditionalDescription(e.target.value)}
              disabled={loadingTopics || !!error}
            />
          </Grid>
          {/* Priority and Due Date - Split Row */}
          {/* <Grid item xs={6}>
            <Select
              label="Task Priority"
              value={taskPriority}
              onChange={(value) => setTaskPriority(value as TaskPriority)}
              options={Object.values(TaskPriority).map((priority) => ({
                key: priority,
                label: formatEnumText(priority),
              }))}
              disabled={loadingTopics || !!error}
            />
          </Grid> */}
          {/* IDs Section - Label Left, Input Right */}
          <Grid item xs={12}>
            <CaseIdentifierField
              actor={user?.userActor || 'FAIRFARE'}
              value={caseIdentifier}
              onChange={(value) => setCaseIdentifier(value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleClose} color="secondary">
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSubmit}
          color="info"
          variant="contained"
          disabled={
            !selectedTopicCategory.trim() ||
            !selectedTopicDescription.trim() ||
            !caseIdentifier.trim() ||
            !!error
          }
        >
          Create Task
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskDialog;
